"use client"

import React, { useState, useEffect, useRef } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/shared/components/ui/dialog'
import { Button } from '@/shared/components/ui/button'
import { X } from 'lucide-react'
import { Textarea } from '@/shared/components/ui/textarea'

interface TextInputModalProps {
    isOpen: boolean
    onClose: () => void
    onSave: (text: string) => void
    initialValue?: string
    imageUrl?: string // Optional thumbnail image URL
    onSavingChange?: (isSaving: boolean) => void // Callback to notify parent when saving state changes
}

/**
 * Text input modal for entering photo descriptions
 */
export default function TextInputModal({
    isOpen,
    onClose,
    onSave,
    initialValue = '',
    imageUrl,
    onSavingChange,
}: TextInputModalProps) {
    const [text, setText] = useState(initialValue)
    const isSavingRef = React.useRef(false) // Track if we're currently saving
    
    // Sync text with initialValue when modal opens; reset when modal closes
    useEffect(() => {
        if (isOpen) {
            setText(initialValue ?? '')
        } else {
            setText('')
            if (isSavingRef.current) {
                isSavingRef.current = false // Reset save flag when modal closes
                onSavingChange?.(false) // Notify parent that saving is complete
            }
        }
    }, [isOpen, initialValue, onSavingChange])

    const handleSave = () => {
        if (text.trim()) {
            isSavingRef.current = true // Mark that we're saving
            onSavingChange?.(true) // Notify parent immediately
            const textToSave = text.trim()
            setText('') // Reset for next use
            // Call onSave, which will handle closing the modal
            // We don't want to close the modal here because that would trigger onClose
            onSave(textToSave)
        }
    }

    const handleCancel = () => {
        if (isSavingRef.current) {
            // Don't cancel if we're in the middle of saving
            return
        }
        setText('')
        isSavingRef.current = false // Reset save flag
        onClose()
    }

    return (
        <Dialog 
            open={isOpen} 
            onOpenChange={(open) => {
                // Only handle close (not open) - but skip if we're saving
                // When saving, the parent sets isOpen to false, but we don't want to trigger cancel
                if (!open && !isSavingRef.current) {
                    handleCancel()
                }
            }}
        >
            {/* Above the photo viewer overlay (z-[100]) so it is usable from the viewer */}
            <DialogContent className="z-[110] sm:max-w-md" hideCloseButton>
                <DialogTitle className="sr-only">Add Description</DialogTitle>
                <DialogDescription className="sr-only">Enter a text description for this photo</DialogDescription>
                <DialogHeader>
                    <div className="flex items-center justify-between">
                        <h2 className="text-lg font-semibold">Add Description</h2>
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={handleCancel}
                            aria-label="Close"
                        >
                            <X className="w-5 h-5" />
                        </Button>
                    </div>
                </DialogHeader>

                <div className="flex flex-col gap-4">
                    {/* Photo thumbnail */}
                    {imageUrl && (
                        <div className="w-24 h-24 rounded-lg overflow-hidden border-2 border-gray-200 mx-auto">
                            {/* eslint-disable-next-line @next/next/no-img-element */}
                            <img
                                src={imageUrl}
                                alt="Photo to describe"
                                className="w-full h-full object-cover"
                            />
                        </div>
                    )}

                    <Textarea
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        placeholder="Enter description for this photo..."
                        className="min-h-[120px] resize-none"
                        autoFocus
                    />

                    <div className="flex gap-2 justify-end">
                        <Button variant="outline" onClick={handleCancel}>
                            Cancel
                        </Button>
                        <Button onClick={handleSave} disabled={!text.trim()}>
                            Done
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    )
}

